import { useState } from 'react'
import { useCreateDeviceModel, useCreateDeviceType, useDeviceTypes } from '../api/queries'

const NEW = '__new__'

function same(a?: string | null, b?: string | null) {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()
}

/**
 * Type og model på en enhed - valgt fra den faste liste (beslutning 105).
 *
 * "+ Ny type" og "+ Ny model" står nederst i rullelisten, så den, der opretter enheden, ikke skal
 * forlade formularen. En type eller model fra før listen står der stadig, markeret "ikke på listen",
 * og kan blive stående. Vælges en model med fabrikat, udfyldes fabrikatet, hvis feltet er tomt.
 */
export function DeviceTypeFields({
  deviceType,
  model,
  manufacturer,
  onChange
}: {
  deviceType: string
  model: string
  manufacturer: string
  onChange: (next: { deviceType: string; model: string; manufacturer: string }) => void
}) {
  const { data: types } = useDeviceTypes()
  const createType = useCreateDeviceType()
  const createModel = useCreateDeviceModel()

  const [addingType, setAddingType] = useState(false)
  const [addingModel, setAddingModel] = useState(false)
  const [newType, setNewType] = useState('')
  const [newModel, setNewModel] = useState('')
  const [newManufacturer, setNewManufacturer] = useState('')
  const [error, setError] = useState<string | null>(null)

  const list = types ?? []
  const type = list.find((t) => same(t.name, deviceType))
  const legacyType = deviceType && !type ? deviceType : null
  const models = type?.models ?? []
  const legacyModel = model && !models.some((m) => same(m.name, model)) ? model : null

  function chooseType(value: string) {
    setError(null)
    if (value === NEW) {
      setAddingType(true)
      return
    }
    setAddingType(false)
    setAddingModel(false)
    // Skiftes typen, passer modellen ikke længere.
    onChange({ deviceType: value, model: same(value, deviceType) ? model : '', manufacturer })
  }

  function chooseModel(value: string) {
    setError(null)
    if (value === NEW) {
      setAddingModel(true)
      return
    }
    setAddingModel(false)
    const picked = models.find((m) => m.name === value)
    onChange({
      deviceType,
      model: value,
      manufacturer: !manufacturer.trim() && picked?.manufacturer ? picked.manufacturer : manufacturer
    })
  }

  async function addType() {
    setError(null)
    try {
      const created = await createType.mutateAsync(newType.trim())
      setAddingType(false)
      setNewType('')
      onChange({ deviceType: created.name, model: '', manufacturer })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Typen kunne ikke gemmes.')
    }
  }

  async function addModel() {
    if (!type) return
    setError(null)
    try {
      const created = await createModel.mutateAsync({
        typeId: type.id,
        name: newModel.trim(),
        manufacturer: newManufacturer.trim() || null
      })
      setAddingModel(false)
      setNewModel('')
      setNewManufacturer('')
      onChange({
        deviceType,
        model: created.name,
        manufacturer: !manufacturer.trim() && created.manufacturer ? created.manufacturer : manufacturer
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Modellen kunne ikke gemmes.')
    }
  }

  return (
    <>
      <div className="field">
        <span className="field__label">Type</span>
        <select value={addingType ? NEW : type?.name ?? deviceType} onChange={(e) => chooseType(e.target.value)}>
          <option value="">Vælg type</option>
          {list.map((t) => (
            <option key={t.id} value={t.name}>
              {t.name}
            </option>
          ))}
          {legacyType && <option value={legacyType}>{legacyType} (ikke på listen)</option>}
          <option value={NEW}>+ Ny type ...</option>
        </select>
        {addingType && (
          <div className="row" style={{ gap: 6, marginTop: 6 }}>
            <input
              autoFocus
              placeholder="f.eks. Nødbelysning"
              value={newType}
              maxLength={100}
              onChange={(e) => setNewType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (newType.trim()) void addType()
                }
              }}
            />
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!newType.trim() || createType.isPending}
              onClick={() => void addType()}
            >
              Tilføj
            </button>
          </div>
        )}
      </div>

      <div className="field">
        <span className="field__label">Model</span>
        <select
          value={addingModel ? NEW : model}
          disabled={!type && !legacyModel}
          onChange={(e) => chooseModel(e.target.value)}
        >
          <option value="">{type ? 'Vælg model' : 'Vælg typen først'}</option>
          {models.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name}
              {m.manufacturer ? ` (${m.manufacturer})` : ''}
            </option>
          ))}
          {legacyModel && <option value={legacyModel}>{legacyModel} (ikke på listen)</option>}
          {type && <option value={NEW}>+ Ny model ...</option>}
        </select>
        {addingModel && (
          <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <input
              autoFocus
              placeholder="Model"
              value={newModel}
              maxLength={100}
              style={{ flex: 1, minWidth: 120 }}
              onChange={(e) => setNewModel(e.target.value)}
            />
            <input
              placeholder="Fabrikat (valgfri)"
              value={newManufacturer}
              maxLength={100}
              style={{ flex: 1, minWidth: 120 }}
              onChange={(e) => setNewManufacturer(e.target.value)}
            />
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!newModel.trim() || createModel.isPending}
              onClick={() => void addModel()}
            >
              Tilføj
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-box" style={{ gridColumn: '1 / -1' }}>
          {error}
        </div>
      )}
    </>
  )
}
